import React, { memo, useEffect, useState } from 'react'
import { Controller, FieldValues, useForm } from 'react-hook-form'

import styled from '@emotion/styled'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import moment from 'moment-timezone'
import { useDebouncedCallback } from 'use-debounce'
import zipState from 'zip-state'

import { SECONDARY_DATE_FORMAT } from '@surglogs/datetime'

import { useAuthContext } from '@surglogs-app/auth'
import { useHandler, useOnDialogBlur } from '@surglogs-app/hooks'
import {
  AccessManagementAccountGetResponse,
  getAccessManagementAccountQueryKey,
} from '@surglogs-app/network'
import { useDefaultBeforeLeavePrompt } from '@surglogs-app/routing'
import {
  Box,
  Button,
  ExpandableBox,
  FlexBox,
  PhoneNumberInput,
  SSNNumberInput,
} from '@surglogs-app/ui'
import { ClearableDateInput } from '@surglogs-app/ui.datepicker'
import {
  DefaultFormField,
  FormField,
  InputField,
  NumberInputField,
} from '@surglogs-app/ui.form'
import { DialogLoader } from '@surglogs-app/ui.loader'
import {
  DefaultModalButtons,
  DefaultModalContent,
} from '@surglogs-app/ui.modal'
import { WrapInToolTip } from '@surglogs-app/ui.tooltip'
import { Country } from '@surglogs-app/util'

import { FORM_FIELDS } from '../constants/FormFields'
import { useUserEditSubmitHandler } from '../hooks/useUserEditSubmitHandler'
import { useAccountSettingsTranslation } from '../services'
import { accountSettingsFormValidator } from '../validators/accountSettingsFormValidator'

const NUMBER_MAX_SCALE = 3
const NUMBER_MAX_PRECISION = 20

interface AccountSettingsFormProps {
  hideModal: () => void
  user: AccessManagementAccountGetResponse | undefined
}

const StyledFormField = styled(Box)`
  margin-bottom: 30px;
  width: 100%;
`

const StyledHalfFormField = styled(Box)`
  margin-bottom: 30px;
  width: 50%;

  &:first-of-type {
    margin-right: 10px;
  }
`

const FormGroup = styled(FlexBox)`
  flex: 1;
  align-items: flex-start;
`

interface AccountSettingsFormValuesModel extends FieldValues {
  email: string
  first_name: string
  last_name: string
  middle_name: string
  other_names: string
  title: string
  cell_number: string
  daytime_number: string
  ssn: string
  npi: number
  dob: string
  address: {
    country: string
    state: string
    city: string
    zip_code: string
    address_line: string
  }
}

export const AccountSettingsForm = memo(
  ({ hideModal, user }: AccountSettingsFormProps) => {
    const queryClient = useQueryClient()
    const { contextDataStore } = useAuthContext()

    const { handleSubmit, register, control, reset, formState, setValue } =
      useForm<AccountSettingsFormValuesModel>({
        mode: 'onChange',
        reValidateMode: 'onChange',
        resolver: yupResolver(accountSettingsFormValidator()),
      })
    useEffect(() => {
      reset(
        Object.assign(
          {},
          ...FORM_FIELDS.map((field) => ({
            [field]: user?.[field] || '',
          })),
        ),
      )
    }, [reset, user])

    const onZipChange = useHandler((e: string) => {
      let stateRes: string | null = ''
      if (e.length < 5) {
        stateRes = null
      } else {
        stateRes = zipState(e)
      }
      setValue('address.state', stateRes ?? '')
      setValue('address.country', stateRes ? Country.UNITED_STATES : '')
    })
    const debouncedOnChange = useDebouncedCallback(onZipChange, 300)
    const { isDirty, errors, isValid } = formState
    const [isSuccessfullySubmitted, setIsSuccessfullySubmitted] =
      useState(false)

    const onSuccess = useHandler(async () => {
      await queryClient.invalidateQueries({
        queryKey: getAccessManagementAccountQueryKey(),
      })
      setIsSuccessfullySubmitted(true)
      contextDataStore.fetchData()
      hideModal()
    })

    const [submit, mutation] = useUserEditSubmitHandler({ onSuccess })
    const { isPending } = mutation
    const { t } = useAccountSettingsTranslation()

    useDefaultBeforeLeavePrompt(() => isDirty && !isSuccessfullySubmitted, {
      onConfirm: hideModal,
      ignoreSilentChanges: false,
    })

    useOnDialogBlur({
      callback: hideModal,
    })

    return (
      <form {...{ onSubmit: handleSubmit(submit) }}>
        {isPending ? <DialogLoader /> : null}
        <DefaultModalContent>
          <FormGroup>
            <StyledFormField>
              <InputField
                {...{
                  ...register('email'),
                  disabled: true,
                  errors,
                  control,
                  formFieldProps: {
                    label: t('email'),
                    t,
                  },
                }}
              />
            </StyledFormField>
          </FormGroup>

          <FormGroup>
            <StyledHalfFormField>
              <InputField
                {...{
                  ...register('first_name'),
                  control,
                  errors,
                  formFieldProps: { t, label: t('first_name') },
                }}
              />
            </StyledHalfFormField>
            <StyledHalfFormField>
              <InputField
                {...{
                  ...register('last_name'),
                  control,
                  errors,
                  formFieldProps: { t, label: t('last_name') },
                }}
              />
            </StyledHalfFormField>
          </FormGroup>

          <FormGroup>
            <StyledHalfFormField>
              <InputField
                {...{
                  ...register('middle_name'),
                  control,
                  errors,
                  formFieldProps: {
                    t,
                    label: {
                      children: t('middle_name'),
                      optional: true,
                    },
                  },
                }}
              />
            </StyledHalfFormField>
            <StyledHalfFormField>
              <InputField
                {...{
                  ...register('other_names'),
                  control,
                  errors,
                  formFieldProps: {
                    t,
                    label: {
                      children: t('other_names'),
                      optional: true,
                    },
                  },
                }}
              />
            </StyledHalfFormField>
          </FormGroup>
          <FormGroup>
            <StyledHalfFormField>
              <InputField
                {...{
                  ...register('title'),
                  control,
                  errors,
                  formFieldProps: {
                    label: t('title'),
                    t,
                  },
                }}
              />
            </StyledHalfFormField>
          </FormGroup>

          <FormGroup>
            <StyledFormField>
              <FormField
                {...{
                  label: { children: t('dob'), optional: true },
                  errors,
                }}>
                <Controller<AccountSettingsFormValuesModel>
                  {...{
                    control,
                    name: 'dob',
                    render: ({ field: { onChange, value, name } }) => (
                      <ClearableDateInput
                        {...{
                          name,
                          value,
                          onChange: (date) =>
                            onChange(
                              moment(date).format(SECONDARY_DATE_FORMAT),
                            ),
                          onClearDateClick: () => onChange(''),
                        }}
                      />
                    ),
                  }}
                />
              </FormField>
            </StyledFormField>
          </FormGroup>

          <ExpandableBox
            {...{
              expandLabel: t('show_optional_details'),
              hideLabel: t('hide_optional_details'),
            }}>
            <FormGroup>
              <StyledHalfFormField>
                <DefaultFormField
                  {...{
                    label: {
                      children: t('daytime_number'),
                      optional: true,
                    },
                    name: 'daytime_number',
                    t,
                    errors,
                  }}>
                  <Controller<AccountSettingsFormValuesModel>
                    {...{
                      control,
                      name: 'daytime_number',
                      render: ({ field: { onBlur, value, onChange } }) => {
                        return (
                          <PhoneNumberInput
                            {...{
                              maxLength: 15,
                              onBlur,
                              onChange,
                              invalid: !!errors?.daytime_number,
                              value,
                              'aria-label': 'day-phone-number',
                            }}
                          />
                        )
                      },
                    }}
                  />
                </DefaultFormField>
              </StyledHalfFormField>
              <StyledHalfFormField>
                <DefaultFormField
                  {...{
                    label: {
                      children: t('cell_number'),
                      optional: true,
                    },
                    name: 'cell_number',
                    t,
                    errors,
                  }}>
                  <Controller<AccountSettingsFormValuesModel>
                    {...{
                      control,
                      name: 'cell_number',
                      render: ({ field: { onBlur, value, onChange } }) => {
                        return (
                          <PhoneNumberInput
                            {...{
                              maxLength: 15,
                              onBlur,
                              onChange,
                              invalid: !!errors?.cell_number,
                              value,
                              'aria-label': 'cell-phone-number',
                            }}
                          />
                        )
                      },
                    }}
                  />
                </DefaultFormField>
              </StyledHalfFormField>
            </FormGroup>

            <FormGroup>
              <StyledHalfFormField>
                <DefaultFormField
                  {...{
                    label: {
                      children: t('ssn'),
                      optional: true,
                    },
                    name: 'ssn',
                    t,
                    errors,
                  }}>
                  <SSNNumberInput {...{ maxLength: 11, ...register('ssn') }} />
                </DefaultFormField>
              </StyledHalfFormField>
              <StyledHalfFormField>
                <NumberInputField
                  {...{
                    control,
                    name: 'npi',
                    integer: true,
                    onlyPositive: true,
                    maxLength: 10,
                    scale: NUMBER_MAX_SCALE,
                    precision: NUMBER_MAX_PRECISION,
                    placeholder: '1234567890',
                    formFieldProps: {
                      t,
                      label: {
                        children: t('npi'),
                        optional: true,
                      },
                    },
                  }}
                />
              </StyledHalfFormField>
            </FormGroup>
            <FormGroup>
              <StyledFormField>
                <InputField
                  {...{
                    ...register('address.address_line'),
                    errors,
                    control,
                    formFieldProps: {
                      t,
                      label: {
                        children: t('address_line'),
                        optional: true,
                      },
                    },
                  }}
                />
              </StyledFormField>
            </FormGroup>
            <FormGroup>
              <StyledHalfFormField>
                <InputField
                  {...{
                    ...register('address.city'),
                    control,
                    errors,
                    formFieldProps: {
                      label: {
                        children: t('city'),
                        optional: true,
                      },
                      t,
                    },
                  }}
                />
              </StyledHalfFormField>
              <StyledHalfFormField>
                <NumberInputField
                  {...{
                    ...register('address.zip_code'),
                    control,
                    maxLength: 5,
                    onChange: debouncedOnChange,
                    invalid: !!errors?.address?.zip_code,
                    formFieldProps: {
                      t,
                      label: {
                        children: t('zip'),
                        optional: true,
                      },
                    },
                  }}
                />
              </StyledHalfFormField>
            </FormGroup>
            <FormGroup>
              <StyledHalfFormField>
                <WrapInToolTip {...{ tooltipText: t('tooltipState') }}>
                  <InputField
                    {...{
                      ...register('address.state'),
                      control,
                      formFieldProps: {
                        label: {
                          children: t('state'),
                          optional: true,
                        },
                        t,
                      },
                      errors,
                      t,
                      disabled: true,
                    }}
                  />
                </WrapInToolTip>
              </StyledHalfFormField>
              <StyledHalfFormField>
                <InputField
                  {...{
                    ...register('address.country'),
                    control,
                    errors,
                    disabled: true,
                    formFieldProps: {
                      t,
                      label: {
                        children: t('country'),
                        optional: true,
                      },
                    },
                  }}
                />
              </StyledHalfFormField>
            </FormGroup>
          </ExpandableBox>
        </DefaultModalContent>

        <DefaultModalButtons>
          <Button
            {...{
              variant: 'success',
              type: 'submit',
              disabled: isPending || !isDirty || !isValid,
            }}>
            {t('save_changes')}
          </Button>
          <Button {...{ onClick: hideModal, variant: 'secondary' }}>
            {t('close')}
          </Button>
        </DefaultModalButtons>
      </form>
    )
  },
)
